"use client";
import { createPortal } from "react-dom";
import { useState, type JSX } from "react";

export interface ModalLauncherComponentRendererProps<T = any> {
    isShown: boolean;
    onClose: (result?: T) => void;
}

export interface ModalLauncherOpenOptions {
    /**
     * Unique id to be used as identifier for the modal contianer
     */
    modalId?: string;
    /**
     * ms before removing the component from the tree
     * @default 300
     */
    delay?: number;
}

/**
 * Portal-based modal launcher that preserves provider context.
 *
 * Usage inside a React component:
 *
 * const { open, portal } = useModalLauncher({ delay: 300 });
 * // In JSX: {portal}
 * // To open:
 * const result = await open(({ isShown, onClose }) => (
 *   <YourModal isOpen={isShown} onClose={() => onClose("done")} />
 * ));
 */
export function useModalLauncher<T = unknown>(options: ModalLauncherOpenOptions = {}) {
    const delay = options.delay ?? 300;

    type State = {
        renderer: (props: ModalLauncherComponentRendererProps<T>) => JSX.Element;
        resolve: (value: T | PromiseLike<T> | undefined) => void;
        isShown: boolean;
    } | null;

    const [state, setState] = useState<State>(null);

    const openPortal = (
        renderer: (props: ModalLauncherComponentRendererProps<T>) => JSX.Element
    ): Promise<T> => {
        return new Promise<T>((resolve) => {
            setState({ renderer, resolve, isShown: true });
        });
    };

    const onClose = (result?: T) => {
        setState((prev) => {
            if (!prev) return prev;
            const next = { ...prev, isShown: false };
            // Delay unmount to allow exit animations
            setTimeout(() => {
                prev.resolve(result);
                setState(null);
            }, delay);
            return next;
        });
    };

    const portal =
        state && document.body
            ? createPortal(state.renderer({ isShown: state.isShown, onClose }), document.body)
            : null;

    return {
        open: openPortal,
        portal,
        isOpen: !!state,
    };
}
